import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Sidebar, FootNav, useTheme } from '@dreamtree-org/twreact-ui';
import { LayoutDashboard, Package, Warehouse, Moon, Sun, Circle, Boxes } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { useAuth, useCan } from '@rbac/useCan';
import { clearAuth } from '@rbac/permissions';
import { buildNav } from '@/routes';
import { useModules } from '@/modules';

const ICONS = {
  'layout-dashboard': LayoutDashboard,
  package: Package,
  warehouse: Warehouse,
};

const Icon = ({ name, size = 18 }) => {
  const C = ICONS[name] || Circle;
  return <C size={size} />;
};

/**
 * Responsive app shell (320px → 2560px):
 *   - Navbar across the top
 *   - Sidebar on >= md, hidden on phones
 *   - FootNav (twreact, mobile-only by design) on phones
 * Nav items come from the module registry and are filtered by the user's
 * (module, action='view') permissions.
 */
export default function DefaultLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const user = useAuth();
  const can = useCan();
  const { toggleTheme, isDark } = useTheme();
  const { fetchApi } = useAppApi();
  const [collapsed, setCollapsed] = useState(false);

  const allowed = buildNav(useModules()).filter((r) => can(r.module, r.action));

  const isActive = (path) => (path === '/' ? loc.pathname === '/' : loc.pathname.startsWith(path));

  const toItem = (r) => ({
    id: r.path,
    label: r.label,
    icon: <Icon name={r.icon} />,
    active: isActive(r.path),
    onClick: () => nav(r.path),
  });

  const logout = async () => {
    try {
      await fetchApi('logout');
    } catch {
      /* the cookie is cleared locally either way */
    }
    clearAuth();
    nav('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar
        logo={
          <span className="flex items-center gap-2 px-2 text-lg font-extrabold tracking-tight text-primary-700">
            <Boxes size={22} className="text-accent-500" />
            Zorzo
          </span>
        }
        user={user ? { name: user.name, email: user.email } : undefined}
        rightMenuContent={
          <button
            onClick={toggleTheme}
            className="rounded-md p-2 hover:bg-gray-100"
            aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        }
        onLogoutClick={logout}
        onLeftIconClick={() => setCollapsed((c) => !c)}
      />

      <div className="flex min-h-0 flex-1">
        <aside
          data-testid="sidebar"
          className="hidden border-r border-[var(--app-border)] bg-[var(--app-surface)] md:block"
        >
          <Sidebar items={allowed.map(toItem)} collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
        </aside>

        <main className="min-w-0 flex-1 overflow-y-auto pt-safe pb-safe-nav md:pb-6">
          <div className="mx-auto w-full max-w-screen-2xl px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      <FootNav items={allowed.slice(0, 5).map(toItem)} className="pb-safe" data-testid="footnav" />
    </div>
  );
}
